import React, { forwardRef, type RefObject } from 'react';

import { useIntl } from 'react-intl-next';

import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { ButtonMenuItem } from '@atlaskit/navigation-system';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import type { NavItemConfigInternal } from '../../../common/types';
import { extendWithCustomizationOptions } from '../../../common/utils/extend-with-customization-options';
import { useOnSave } from '../../../common/utils/use-on-save';
import { useUserMenuDataShapes } from '../../../controllers/user-state/user-menu-data-shapes-provider';
import { customizationModalEntryPoint } from '../../customization-modal/entrypoint';

import { messages } from './messages';
import { useGetMenuIconAndLabel } from './use-get-menu-icon-and-label';

type CustomizeSidebarMenuItemProps = {
	appLabel: string;
	items: Record<string, NavItemConfigInternal>;
	onClick?: () => void;
	returnFocusRef?: RefObject<HTMLElement>;
	shortcuts: Record<string, GlobalShortcutsItem>;
	testId?: string;
};

export const CustomizeSidebarMenuItem = forwardRef(CustomizeSidebarMenuItemWithRef);

function CustomizeSidebarMenuItemWithRef(
	{ appLabel, items, onClick, returnFocusRef, shortcuts, testId }: CustomizeSidebarMenuItemProps,
	ref: React.Ref<any>,
) {
	const getMenuIconAndLabel = useGetMenuIconAndLabel(items, shortcuts);
	const { itemUserMenuData, shortcutUserMenuData } = useUserMenuDataShapes();
	const itemExtendedUserMenuData = extendWithCustomizationOptions(itemUserMenuData, items);

	const onSave = useOnSave();
	const { formatMessage } = useIntl();

	return (
		<ModalTrigger
			entryPoint={customizationModalEntryPoint}
			entryPointProps={{
				appLabel,
				getMenuIconAndLabel,
				itemExtendedUserMenuData,
				onSave,
				returnFocusRef,
				shortcutExtendedUserMenuData: shortcutUserMenuData,
			}}
		>
			{({ ref: triggerRef }) => (
				<ButtonMenuItem
					elemBefore={<CustomizeIcon label="" />}
					ref={mergeRefs([triggerRef, ref])}
					testId={testId}
					onClick={onClick}
				>
					{formatMessage(messages.customizeSidebarMenuItemLabel)}
				</ButtonMenuItem>
			)}
		</ModalTrigger>
	);
}
